(ns manatee.domain.item
  (:require
   #?@(:clj [[bedrock-sql.honey :as honey]
             [cheshire.core :as json]])
   [clojure.set :as set]
   [clojure.spec.alpha :as s]
   [manatee.domain.edit :as edit]
   [manatee.domain.external :as external]
   [manatee.domain.task :as task]
   [manatee.domain.task.spec :as task-spec]
   [manatee.spec.core :as core]
   [medley.core :as medley]
   [spec-tools.data-spec :as ds]
   [taoensso.timbre :as log]
   [utility-belt.uuid :as uuid]))

(s/def ::id uuid?)
(s/def ::item-type #{:answer :part})
(s/def ::part-number ::core/pos-number)

(s/def ::item-group-id uuid?)
(s/def ::task-config-id uuid?)
(s/def ::created string?)

(s/def ::status #{"success" "error"})
(s/def ::answer-id uuid?)
(s/def ::answer-ids (s/coll-of ::answer-id :min-count 1))
(s/def ::question-id uuid?)
(s/def ::text-question-number int?)
(s/def ::question-number (s/or :speech-question-number ::external/question-number
                               :text-question-number ::text-question-number))
(s/def ::submission-id uuid?)

(s/def ::name
  string?)

(s/def ::link
  (s/nilable string?))

(s/def ::task-document
  (s/keys :req-un [::id
                   ::name
                   ::link]))

(s/def ::task-documents
  (s/nilable (s/coll-of ::task-document)))

(def general-item-spec-template
  {:id ::id
   :item-group-id ::item-group-id
   (ds/opt :created) ::created
   :item-type ::item-type
   :task-config-id ::task-config-id
   :account-id ::external/account-id
   :submission-id ::submission-id
   :part ::part-number
   (ds/opt :feedback) ::edit/feedback
   (ds/opt :deferred?) boolean?
   (ds/opt :task-documents) ::task-documents})

(defn base-item-spec
  [item-type {:keys [id] :as task-config}]
  (merge general-item-spec-template
         {:item-type (s/spec #{item-type})
          :task-config-id (s/spec #{(if (uuid? id) id (uuid/uuid-from-string id))})
          :medium (s/spec #{(task/medium task-config)})}))

(defn part-label
  [part-number task-name]
  (str "Part " part-number ": " task-name))

(defn part-item-score
  "The min and max auto scores for an item part"
  [min max]
  (s/def ::score (s/double-in :min min :max max :NaN? false :infinite? false)))

(defn part-item-spec
  [task-config {:keys [medium] :as _prototype-item}]
  (let [min (get-in task-config [:data :part :item-score :min])
        max (get-in task-config [:data :part :item-score :max])]
    (merge (base-item-spec :part task-config)
           {:answer-ids ::answer-ids
            (ds/opt :status) ::status
            (ds/opt :auto-scores) map?}
           (case medium
             :text {(ds/opt :score) number?}
             (when (and min max) {(ds/opt :score) (part-item-score min max)})))))

(defn answer-item-spec
  [task-config {:keys [medium] :as _prototype-item}]
  (let [{:keys [field-name] :as field} (task/script-annotation-field task-config)]
    (merge (base-item-spec :answer task-config)
           {:answer-id ::answer-id
            (ds/opt :question-id) ::question-id
            (ds/opt :question-number) ::question-number}
           (case medium
             :text {(ds/opt :score) number?}
             {})
           (when field
             (task-spec/field-spec field-name field)))))

(defn item-spec
  "An optional prototype item can be passed to restrict the spec output
   to the same item-type (:part or :answer) and medium (:text or :speech)."
  ([task-config]
   (item-spec task-config nil))
  ([{:keys [task-name id] :as task-config} {:keys [item-type] :as prototype-item}]
   (ds/spec
    {:name (keyword (str *ns*) (str task-name "-" id "-" (name (or item-type ""))))
     :spec (case item-type
             :part (part-item-spec task-config prototype-item)
             :answer (answer-item-spec task-config prototype-item)
             (ds/or {:part (part-item-spec task-config prototype-item)
                     :answer (answer-item-spec task-config prototype-item)}))})))

(defn- extract-data-part
  [{:keys [answer-ids score status] :as _data}]
  (merge {:answer-ids (mapv uuid/uuid-from-string answer-ids)}
         (when score
           {:score score})
         (when status
           {:status status})))

(defn- extract-data-answer
  [task-config {:keys [answer-id question-id question-number] :as data}]
  (let [{:keys [field-name]} (task/script-annotation-field task-config)
        field-value (get data field-name)]
    (merge
     {:answer-id (uuid/uuid-from-string answer-id)}
     (when question-id
       {:question-id (uuid/uuid-from-string question-id)})
     (when question-number
       {:question-number question-number})
     (when (and field-name field-value)
       {field-name (edit/coerce-script field-value)}))))

(defn- extract-data
  [task-config item-type data]
  (let [{:keys [part account-id submission-id]} data]
    (merge
     {:account-id account-id
      :submission-id (uuid/uuid-from-string submission-id)
      :part part}
     (case item-type
       :part   (extract-data-part data)
       :answer (extract-data-answer task-config data)))))

(defn coerce
  [task-config
   {:keys [medium created item-type id task-config-id item-group-id data feedback deferred? task-documents] :as _item}]
  (merge {:id             id
          :item-group-id  item-group-id
          :medium         medium
          :item-type      (keyword item-type)
          :task-config-id task-config-id}
         (when task-documents
           {:task-documents task-documents})
         (when (some? deferred?)
           {:deferred? deferred?})
         (when (:auto-scores data)
           {:auto-scores (:auto-scores data)})
         (when (:score data)
           {:score (:score data)})
         (when feedback
           {:feedback feedback})
         (when created
           ; No created time on items constructed from submissions.
           ; It is added in the db not code
           {:created (str created)})
         (when (:submission-age-days data)
           {:submission-age-days (:submission-age-days data)})
         (extract-data task-config
                       (keyword item-type)
                       data)))

(defn coerce-item-group
  [group]
  (-> (set/rename-keys group {:deferred :deferred?
                              :has-edits :has-edits?
                              :reported :reported?
                              :task :item-task})
      (update :deferred? boolean)
      (update :has-edits? boolean)
      (update :reported? boolean)))

(defn validate
  "Validate an item, logging an error if the item is invalid and optionally throwing an exception"
  [task-config item throw?]
  (let [spec (item-spec task-config item)]
    (when-not (s/valid? spec item)
      (let [invalid-map {:cause :invalid-item
                         :item item
                         :task-config task-config
                         :spec spec
                         :reason-data (s/explain-data spec item)
                         :reason-string (s/explain-str spec item)}]
        (log/error "Invalid Item" invalid-map)
        (when throw?
          (throw (ex-info "Invalid Item" invalid-map)))))))

(defn coerce-and-validate
  [task-config item]
  (let [coerced-item (coerce task-config item)]
    (validate task-config coerced-item false)
    coerced-item))

; maximum number of times an item can be deferred
(def defer-limit 8)

; number of annotations that must be made before a deferred item will reappear
(def annotations-since-deferral 10)

(defn format-item
  [item]
  (update item :item-type keyword))

(defn format-feedback-item
  [item]
  (-> item
      (format-item)
      (medley/update-existing-in [:feedback :status] keyword)
      (assoc-in [:feedback :id] (:feedback-id item))
      (dissoc :feedback-id)))

#?(:clj
   (defn format-item-for-insert
     [item]
     (let [speech-item (= :speech (:medium item))]
       (-> item
           (update :item-type #(honey/cast (name %) :item-type))
           (update :task-config-id uuid/str->uuid)
           (update :item-group-id uuid/str->uuid)
           (update :id uuid/str->uuid)
           (update :data (comp honey/cast-to-jsonb json/encode))
           (update :medium #(honey/cast (name %) :medium-type))
           (cond->
            speech-item (assoc :speech-submission-id (uuid/str->uuid (get-in item [:data :submission-id])))
            speech-item (assoc :speech-account-id (get-in item [:data :account-id])))))))

